import React from 'react';
import { LegalShell, Section, List } from '../../components/legal/LegalShell';

const TermsPage = () => (
  <LegalShell eyebrow="Legal" title="Terms & Conditions" updated="07 August 2026">
    <p>These Terms &amp; Conditions apply to the maintenance service plans and related services provided by Domestic Home Services Limited to customers in the United Kingdom.</p>
    <p>Please read these Terms carefully. Together with your Welcome Letter, Plan Certificate, Invoice and any other plan-specific documents provided to you, they form the agreement between you and Domestic Home Services Limited</p>

    <Section n="1" title="About Us">
      <p>Domestic Home Services Limited is a company registered in England and Wales under company number 17368184.</p>
      <p><b>Registered Office:</b><br/>128 City Road<br/>London<br/>United Kingdom<br/>EC1V 2NX</p>
      <p>Telephone: +44 (0)113 548 7557<br/>Email: hello@domestichomeservices.com</p>
      <p>Domestic Home Services Limited provides home maintenance service plans designed to assist customers with the repair and maintenance of eligible household appliances and home systems.</p>
      <p>Repair and maintenance services may be provided through suitably selected independent third-party contractors and engineers.</p>
    </Section>

    <Section n="2" title="Your Maintenance Service Plan">
      <p>Your maintenance service plan provides access to repair and maintenance assistance for the items specifically listed on your Plan Certificate.</p>
      <p>Your plan becomes active once we have received the required cleared payment, subject to any applicable qualifying period stated in your plan documents.</p>
      <p>Unless your Plan Certificate states otherwise, plans are provided for a period of 12 months.</p>
      <p>You are responsible for ensuring that all information supplied to us is complete and accurate, including information relating to your address and the appliances or systems included within your plan.</p>
      <p>Plans are intended for domestic residential use within the United Kingdom.</p>
      <p>Covered items must be in reasonable working condition when the plan is purchased unless we have expressly agreed otherwise in writing.</p>
    </Section>

    <Section n="3" title="Qualifying Period">
      <p>Unless otherwise stated in your Plan Certificate, a 30-day qualifying period applies from the commencement date of your plan.</p>
      <p>Faults that existed before joining, were developing before the plan commenced, or became apparent during the qualifying period may not be eligible for assistance under the plan.</p>
      <p>The qualifying period helps ensure that our maintenance plans are used for future and unexpected maintenance requirements rather than existing faults.</p>
    </Section>

    <Section n="4" title="What's Included">
      <p>Depending upon the maintenance plan you have selected, services may include:</p>
      <List items={[
        'Assessment of reported faults.',
        'Repair assistance for eligible covered items.',
        'Engineer call-outs where appropriate.',
        'Labour associated with authorised repairs.',
        'Replacement parts required for authorised repairs, subject to availability and plan limits.',
        'Customer support throughout the duration of your plan.',
      ]} />
      <p>The exact appliances, systems and services included in your plan will be stated in your Plan Certificate or Welcome Letter.</p>
      <p>All repair requests are subject to assessment, eligibility and authorisation under these Terms.</p>
    </Section>

    <Section n="5" title="Repairs">
      <p>When you report a fault, we will assess the information provided and determine the appropriate next steps.</p>
      <p>Where appropriate, we may arrange for an independent engineer or contractor to inspect or repair the covered item.</p>
      <p>Repairs must be authorised by Domestic Home Services Limited before work is undertaken.</p>
      <p>We will not normally reimburse the cost of repairs independently arranged by a customer without our prior written approval.</p>
      <p>The availability and timing of engineers may vary depending upon location, parts availability, the nature of the fault and circumstances outside our reasonable control.</p>
    </Section>

    <Section n="6" title="Replacement or Alternative Resolution">
      <p>If an eligible covered item cannot reasonably or economically be repaired, we may, depending on the circumstances and applicable plan limits:</p>
      <List items={[
        'Arrange an alternative repair solution;',
        'Offer a contribution towards replacement;',
        'Provide a replacement item; or',
        'Offer another reasonable resolution.',
      ]} />
      <p>Any replacement or contribution will be determined after assessment and is subject to the terms, limitations and maximum liability applicable to the plan.</p>
      <p>Where a replacement is offered, we will aim to provide an item with reasonably comparable functionality. An identical brand, model, specification, appearance or colour cannot be guaranteed.</p>
    </Section>

    <Section n="7" title="What's Not Covered">
      <p>Unless expressly agreed otherwise, the following are not covered:</p>
      <List items={[
        'Pre-existing faults.',
        'Faults developing before the plan commenced.',
        'Faults occurring during an applicable qualifying period.',
        'Cosmetic damage that does not materially affect normal operation.',
        'Accidental or deliberate damage unless specifically included in your plan.',
        'Damage caused by misuse, neglect or abuse.',
        'Damage resulting from unauthorised repair or modification.',
        'Failure to follow manufacturer instructions.',
        'Routine cleaning or maintenance.',
        'Consumable items.',
        'Commercially used appliances.',
        'Appliances or systems not listed on your Plan Certificate.',
        'Repairs arranged without our prior authorisation.',
        'Damage arising from fire, flooding, theft, malicious damage or other events outside the normal scope of a maintenance service plan.',
        'Costs or losses unrelated to the repair of the covered item.',
      ]} />
    </Section>

    <Section n="8" title="Beyond Economic Repair">
      <p>Where an appliance or system is considered beyond economic repair, we will review the available options with you.</p>
      <p>Depending upon the appliance, its age, condition, original purchase value, availability of parts and the terms of your plan, we may offer a repair alternative, replacement contribution or another reasonable resolution.</p>
      <p>For boilers considered beyond economic repair or otherwise not reasonably repairable, any contribution towards replacement may be subject to a maximum contribution of £200, unless your Plan Certificate specifically provides a different amount.</p>
    </Section>

    <Section n="9" title="Limits of the Plan">
      <p>Our responsibility under a maintenance service plan is limited to providing the services described within the plan and these Terms.</p>
      <p>Unless your Plan Certificate specifies a different limit, our maximum responsibility in respect of an individual covered appliance will not exceed its reasonable original purchase value.</p>
      <p>We will not be responsible for indirect or consequential losses arising from the breakdown of an appliance or system, except where liability cannot legally be excluded or restricted.</p>
      <p>Nothing within these Terms limits or excludes any statutory rights or liabilities that cannot legally be limited or excluded.</p>
    </Section>

    <Section n="10" title="Payment">
      <p>The price and payment schedule applicable to your maintenance service plan will be shown on your Invoice, Welcome Letter or Plan Certificate.</p>
      <p>Payment must be made using the payment method communicated to you by Domestic Home Services Limited</p>
      <p>Where payment is requested by bank transfer, customers should use their Plan Number or Invoice Number as the payment reference wherever possible.</p>
      <p>Your plan will not become active until the required initial payment has cleared.</p>
      <p>Failure to make an agreed payment may result in your plan being suspended or cancelled.</p>
      <p>We will endeavour to contact you before cancelling a plan due to non-payment.</p>
    </Section>

    <Section n="11" title="Cooling-Off Period">
      <p>You have 14 days from entering into the agreement to change your mind and request cancellation.</p>
      <p>If you cancel during this period and no repair, engineer visit or other service has been requested or provided, payments received may be refunded in full.</p>
      <p>If you have specifically requested that services begin during the cooling-off period, we may deduct reasonable costs for services already provided where permitted by law.</p>
      <p>Your statutory rights are not affected.</p>
    </Section>

    <Section n="12" title="Cancellation After the Cooling-Off Period">
      <p>You may request cancellation of your plan after the 14-day cooling-off period by contacting us.</p>
      <p>Depending upon the circumstances and your plan, an administration charge may apply.</p>
      <p>Where applicable, the administration charge will be communicated to you and will normally fall between £45 and £70, depending upon the plan and administrative work involved.</p>
      <p>Any refund entitlement will take into consideration payments received, the period for which services were available, services already supplied and other amounts properly due under the agreement.</p>
      <p>Further information may be provided within our Refund and Cancellation Policy.</p>
    </Section>

    <Section n="13" title="Cancellation by Us">
      <p>We may suspend or cancel a maintenance service plan where:</p>
      <List items={[
        'Payments remain outstanding.',
        'False or misleading information has been provided.',
        'There is suspected fraud or misuse.',
        'The plan is being used contrary to these Terms.',
        'A customer behaves abusively or threateningly towards employees, contractors or representatives.',
        'Continuing to provide the service would be unlawful or unreasonable.',
      ]} />
      <p>Where reasonably possible, we will communicate with the customer before taking such action.</p>
    </Section>

    <Section n="14" title="Complaints">
      <p>We want every customer to receive a professional and satisfactory service.</p>
      <p>If something has gone wrong, please contact our Customer Care Team:</p>
      <p>Telephone: +44 (0)113 548 7557<br/>Email: hello@domestichomeservices.com</p>
      <p>We will acknowledge complaints promptly and investigate them fairly and impartially.</p>
      <p>We aim to provide a resolution as quickly as reasonably possible and will keep customers informed where additional time is required.</p>
      <p>Complaints and customer feedback are also reviewed internally to identify areas where our services can be improved.</p>
      <p>Further information is available within our Complaints Procedure and Compliance Policy.</p>
    </Section>

    <Section n="15" title="Vulnerable Customers">
      <p>We recognise that some customers may require additional assistance because of their personal circumstances.</p>
      <p>We encourage customers to tell us if they require additional support when communicating with us or using our services.</p>
      <p>Where reasonably possible, we will adapt our communication and customer support to accommodate individual needs.</p>
      <p>Customers may also nominate an authorised representative to communicate with us on their behalf, subject to appropriate verification and consent.</p>
    </Section>

    <Section n="16" title="Data Protection & Privacy">
      <p>We respect and protect the privacy of our customers.</p>
      <p>Personal information is processed in accordance with applicable UK data protection legislation and our Privacy Policy.</p>
      <p>Information may be shared with third parties where reasonably necessary to provide our services, including:</p>
      <List items={[
        'Repair engineers and contractors.',
        'Technology and communications providers.',
        'Payment and banking service providers.',
        'Professional advisers.',
        'Government, regulatory or law-enforcement authorities where legally required.',
      ]} />
      <p>We only share information that is reasonably necessary for the relevant purpose.</p>
      <p>We do not sell customer personal information.</p>
    </Section>

    <Section n="17" title="Independent Contractors">
      <p>Domestic Home Services Limited may use independent third-party contractors and engineers to provide repair and maintenance services.</p>
      <p>We seek to work with contractors who have appropriate experience and capability for the services they provide.</p>
      <p>Contractor availability may vary according to geographic location, workload and the nature of the repair required.</p>
    </Section>

    <Section n="18" title="Website Use">
      <p>Information available through our website is provided for general information about our company, maintenance plans and services.</p>
      <p>You must not use our website:</p>
      <List items={[
        'For fraudulent purposes.',
        'To provide intentionally false information.',
        'To interfere with the security or operation of the website.',
        'To attempt unauthorised access to our systems.',
        'In any manner contrary to applicable law.',
      ]} />
      <p>We take reasonable steps to maintain the availability and security of our website but cannot guarantee uninterrupted access at all times.</p>
    </Section>

    <Section n="19" title="Events Outside Our Control">
      <p>We will not be responsible for delays caused by circumstances outside our reasonable control.</p>
      <p>These may include:</p>
      <List items={[
        'Severe weather.',
        'Natural disasters.',
        'Transport disruption.',
        'Supplier disruption.',
        'Shortages of replacement parts.',
        'Manufacturer delays.',
        'Telecommunications failures.',
        'National emergencies.',
        'Government restrictions.',
        'Other circumstances reasonably beyond our control.',
      ]} />
      <p>Where such circumstances arise, we will make reasonable efforts to minimise disruption to customers.</p>
    </Section>

    <Section n="20" title="Changes to These Terms">
      <p>We may update these Terms periodically to reflect changes to our services, business operations or applicable legal requirements.</p>
      <p>Where a material change affects an existing customer's plan, we will provide reasonable notice where appropriate.</p>
      <p>Changes will not remove statutory rights available to customers under applicable law.</p>
    </Section>

    <Section n="21" title="Governing Law">
      <p>These Terms &amp; Conditions are governed by the laws of England and Wales.</p>
      <p>Any dispute will be subject to the jurisdiction applicable under English and Welsh law, without affecting any mandatory consumer rights regarding where proceedings may legally be brought.</p>
    </Section>

    <Section n="22" title="Contact Us">
      <p>If you have questions regarding your maintenance service plan, these Terms &amp; Conditions, a repair request or a complaint, please contact:</p>
      <p><b>Domestic Home Services Limited</b><br/>Registered Office:<br/>128 City Road<br/>London<br/>United Kingdom<br/>EC1V 2NX</p>
      <p>Company Number: 17368184<br/>Telephone: +44 (0)113 548 7557<br/>Email: hello@domestichomeservices.com</p>
      <p>Domestic Home Services Limited reserves all rights available to it under these Terms and applicable law while remaining committed to treating customers fairly, transparently and professionally.</p>
    </Section>
  </LegalShell>
);

export default TermsPage;
